import * as members from '../../privates/privateMembers';
import {validate} from '../../privates/validations/validation';

/**
 * @mixin ClickableMixin
 * @summary Provides functionality for elements that can be clicked.
 * @description
 *  This mixin provides event handlers that run when an element is clicked or double-clicked.
 * 
 *  **About Mixins**
 * 
 *  [Mixins](https://support.wix.com/en/article/velo-about-mixins) provide functionality that other elements can inherit and use. 
 *  
 *  Mixins are not elements. You cannot add mixins to a page in the Editor like other `$w` elements, and mixins are not meant to be used directly 
 *  in your code. 
 *  
 *  For example, you would not write code like this, because it is out of context: 
 * 
 *  ```javascript
 *  $w("#ClickableMixin").onClick( (event) => {
 *     let targetId = event.target.id;
 *    }
 *  );
 *  ```
 * 
 *  Instead, you can code the following if `myButton` is an element that can be clicked and  
 *  "mixes in" `ClickableMixin`. 
 * 
 *   ```javascript
 *  $w("#myButton").onClick( (event) => {
 *     let targetId = event.target.id;
 *    }
 *  );
 *  ```
 *   
 * @memberof $w
 */

class ClickableMixin {

    /**
     * @function onClick
     * @syntax
     * function onClick(handler: MouseEventHandler): Element
     * callback MouseEventHandler(event: MouseEvent, $w: Function): void
     * @summary Adds an event handler that runs when the element is clicked.
     * @description
     * 
     *  An element receives a `click` event when a user clicks on the element
     *  and releases.
     *
     *  When a user double-clicks an element, two `click` events are fired
     *  before a `doubleClick` event is also fired.
     * 
     *  You can also [define an event handler using the Properties and Events panel](https://support.wix.com/en/article/velo-reacting-to-user-actions-using-events).
     *  
     * ![Properties and Events panel](images/properties_and_events.png "Properties and Events panel")
     * 
     *  This will automatically add the required code for the selected element and event:
     * 
     *  ```javascript 
     *     export function button1_click(event) {
	   *        // This function was added from the Properties & Events panel. To learn more, visit http://wix.to/UcBnC-4
	   *        // Add your code for this event here: 
     *      }  
     * ```
     * 
     * 
     * > **Note:** Do not use the Editor Link panel to redirect on click when a link is already 
     *  defined using the `onClick()` function. To avoid unpredictable behavior, remove 
     *  the link from the Editor Link panel.
     * >
     * >
     * > **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  [here](https://www.wix.com/velo/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers).
     *   
     * @param {$w.MouseEventHandler} handler The name of the function or
     *  the function expression to run when the element is clicked.
     * @returns {$w.Element} The element to which the event handler was added.
     * @snippet [ClickableMixin-onClick.es6=Get the ID of the element that was clicked]
     * @snippet [onClick-get_coordinates.es6=Get a mouse click's coordinates]
     * @eventtype click
     * @see [onDblClick( )](#onDblClick)
     * @memberof $w.ClickableMixin
     * @eventhandler
     */
    onClick(cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onClick',
            dataToValidate: {onClick: cb},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().CLICK, controllerId, cb);
        return this;
    }

    /**
     * @function onDblClick
     * @syntax
     * function onDblClick(handler: MouseEventHandler): Element
     * callback MouseEventHandler(event: MouseEvent, $w: Function): void
     * @summary Adds an event handler that runs when the element is double-clicked.
     * @description
     *  An element receives a `dblClick` event when a user double-clicks on the element
     *  and releases.
     *
     *  When a user double-clicks an element, two `click` events are fired
     *  before a `doubleClick` event is also fired.
     * 
     *  You can also [define an event handler using the Properties and Events panel](https://support.wix.com/en/article/velo-reacting-to-user-actions-using-events).
     * 
     * 
     *  > **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  [here](https://www.wix.com/velo/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers).
     * @param {$w.MouseEventHandler} handler The name of the function or
     *  the function expression to run when the element is clicked.
     * @returns {$w.Element} The element to which the event handler was added.
     * @snippet [ClickableMixin-onDblClick.es6=Get the ID of the element that was double-clicked]
     * @see [onClick( )](#onClick)
     * @memberof $w.ClickableMixin
     * @eventtype dblClick
     * @eventhandler
     */
    onDblClick(cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onDblClick',
            dataToValidate: {onDblClick: cb},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().DBL_CLICK, controllerId, cb);
        return this;
    }
}

export default ClickableMixin;
